import { h, Fragment } from 'preact';

import MarkdownDisplay from '../gui/markdownDisplay';
import Poll from '../poll';
import FactBox from './factBox';
import ReadAlsoBox from './readAlsoBox';
import ResultsList from './resultsList';
import ProductBox from './productBox';
// The {{events}} widget is C and comes from the product registry (stubbed to {}
// in the Dyrejournal export). Access members at render time only — the registry
// sits in an import cycle and is not fully evaluated at module scope.
import componentsOther from '../../product/components-other';
import localUtil from '../../lib/util';
import { splitBodySegments, addBylinesToImages, fixImageSrc } from './articleContentProcessor';

const VIDEO_FILE_RE = /\.(m4v|m4a|mp4|mov)$/i;

export function articleVideoFiles(article = {}) {
    return (article.files || []).filter(f => f.s3LinkStream || (f.s3Link && VIDEO_FILE_RE.test(f.s3Link)));
}

/**
 * Render an article body (markdown + {{markers}}) exactly the way the public
 * article view does. Shared by the article view and the editor live preview.
 *
 * `renderVideoFile(file)` and `renderArticleMap(article)` are injected because
 * the view renders a real player / Leaflet map while the preview renders
 * lightweight placeholders. Omitting them skips that segment.
 */
export default function renderArticleBody({
    article = {},
    body,
    language = 'en',
    stores,
    renderVideoFile,
    renderArticleMap,
}) {
    const displayBody = body != null ? body : (article.body || '');
    if (!displayBody) return null;

    const markdown = (text, key) => (
        <MarkdownDisplay
            key={key}
            markdown={fixImageSrc(localUtil.replaceImages(text, article.images, language, false))}
            markedOpts={localUtil.getMarkdownOptions()}
            stores={stores}
            articleId={article.id}
        />
    );

    const segments = splitBodySegments(displayBody);
    const hasPollMarker = segments.some(s => s.type === 'poll');
    const hasMarkers = segments.length > 1 || segments[0]?.type !== 'markdown';
    const hasBylines = article.images?.some(img => img?.caption || img?.photographer);
    const poll = article.polls?.[0];

    // No markers and no bylines: split body into paragraphs and insert poll mid-article
    if (!hasMarkers && !hasBylines) {
        if (!poll) {
            return markdown(displayBody);
        }
        const paragraphs = displayBody.split(/\n\n+/).filter(p => p.trim());
        const insertAfter = Math.max(1, Math.ceil(paragraphs.length / 2));
        const firstHalf = paragraphs.slice(0, insertAfter).join('\n\n');
        const secondHalf = paragraphs.slice(insertAfter).join('\n\n');
        return (<>
            {markdown(firstHalf, 'first')}
            <Poll poll={poll} stores={stores} />
            {secondHalf && markdown(secondHalf, 'second')}
        </>);
    }

    // Has markers or bylines: render segments, insert poll mid-article if no {{poll}} marker
    const pollInsertIdx = !hasPollMarker && poll
        ? Math.max(1, Math.ceil(segments.length / 2))
        : -1;
    return (<>
        {segments.map((segment, idx) => {
            const rendered = [];
            if (segment.type === 'markdown') {
                let processed = fixImageSrc(localUtil.replaceImages(segment.content, article.images, language, false));
                if (hasBylines) {
                    processed = addBylinesToImages(processed, article.images);
                }
                rendered.push(<MarkdownDisplay key={`seg-${idx}`} markdown={processed} markedOpts={localUtil.getMarkdownOptions()} stores={stores} articleId={article.id} />);
            } else if (segment.type === 'factbox') {
                rendered.push(<FactBox key={`fb-${segment.articleId}`} articleId={segment.articleId} stores={stores} />);
            } else if (segment.type === 'lesogsa') {
                rendered.push(<ReadAlsoBox key={`la-${segment.articleId}`} articleId={segment.articleId} stores={stores} />);
            } else if (segment.type === 'poll' && poll) {
                rendered.push(<Poll key={`poll-${idx}`} poll={poll} stores={stores} />);
            } else if (segment.type === 'video') {
                const videoFile = articleVideoFiles(article)[segment.videoIndex];
                if (videoFile && renderVideoFile) {
                    rendered.push(<div key={`video-${idx}`} class='my-3'>{renderVideoFile(videoFile)}</div>);
                }
            } else if (segment.type === 'map' && article.mapMarkers?.length > 0) {
                if (renderArticleMap) {
                    rendered.push(renderArticleMap(article));
                }
            } else if (segment.type === 'results') {
                const rl = article.resultsLists?.[segment.resultsIndex];
                if (rl) {
                    rendered.push(<ResultsList key={`results-${idx}`} resultsList={rl} stores={stores} />);
                }
            } else if (segment.type === 'product') {
                rendered.push(<ProductBox key={`prod-${segment.productId}`} productId={segment.productId} stores={stores} />);
            } else if (segment.type === 'events') {
                if (componentsOther.EventsWidget) {
                    rendered.push(
                        <componentsOther.EventsWidget
                            key={`events-${idx}`}
                            calendarIds={segment.calendarIds}
                            searchText={segment.searchText}
                            customerId={article.customer}
                            stores={stores}
                        />
                    );
                }
            }
            // Insert poll after this segment if it's the midpoint
            if (pollInsertIdx === idx + 1) {
                rendered.push(<Poll key='poll-auto' poll={poll} stores={stores} />);
            }
            return rendered;
        })}
    </>);
}
